import React, { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: { home: "Home", services: "Services", about: "About", portfolio: "Portfolio", blog: "Blog", contact: "Contact", book: "Book a call" },
    common: { learnMore: "Learn more", getStarted: "Get started", talkToUs: "Talk to us", readMore: "Read more" },
    hero: {
      kicker: "Germany · AI · Automation",
      title: "Automate. Innovate. Elevate.",
      subtitle: "Transforming businesses through AI automation, web development, digital marketing, branding, and scalable digital solutions — made in Berlin.",
      cta1: "Free strategy call",
      cta2: "Explore services",
      stat1: "Projects shipped",
      stat2: "Avg. response time",
      stat3: "Client retention",
      trustedBy: "Trusted by ambitious teams",
      trust: ["Berlin Based", "AI-Powered Solutions", "End-to-End Digital Services"],
    },
    services: {
      kicker: "What we do",
      title: "Digital solutions that make an impact.",
      subtitle: "From AI agents that handle real work to high-converting web platforms — one partner, end-to-end.",
      ai: { title: "AI & Automation", desc: "Automated processes, intelligent workflows and efficiency gains." },
      web: { title: "Web Development", desc: "Modern, fast and scalable websites for businesses of every size." },
      marketing: { title: "Digital Marketing", desc: "Data-driven SEO, content strategy and performance marketing for sustainable growth." },
      content: { title: "Content & Branding", desc: "Strong brand identities, creative content and compelling storytelling." },
      brand: { title: "Brand Design", desc: "Visual identities that build trust and drive recognition." },
      consulting: { title: "Strategy & Consulting", desc: "Digital strategies for long-term business success." },
      capabilities: "Capabilities",
    },
    process: {
      kicker: "How we work",
      title: "A structured process. Clear results.",
      steps: [
        { t: "Discovery", d: "We analyze goals and uncover opportunity." },
        { t: "Strategy", d: "We develop a digital roadmap." },
        { t: "Design", d: "Modern UX/UI design with a focus on conversion." },
        { t: "Build", d: "Clean, performant and scalable implementation." },
        { t: "Launch", d: "Testing, optimization and a successful go-live." },
      ],
    },
    whyUs: {
      kicker: "Why us",
      title: "Why Joshi FusionTech Media?",
      intro: "We combine technology, creativity and strategy to build digital solutions that grow businesses sustainably.",
      points: [
        { t: "Tailored solutions", d: "Custom-fit to your business — no templates, no compromises." },
        { t: "Modern technology", d: "Current stack: React, Python, AI APIs and cloud-native architecture." },
        { t: "AI-powered automation", d: "Workflows that don't just save costs — they generate active value." },
        { t: "Transparent communication", d: "Weekly demos, clear roadmap, no black box." },
        { t: "Long-term partnerships", d: "We build relationships, not just projects — post-launch support included." },
      ],
    },
    stats: {
      kicker: "Numbers that matter",
      title: "Results we're proud of.",
      items: [
        { value: 50, suffix: "+", label: "Projects completed" },
        { value: 95, suffix: "%", label: "Client satisfaction" },
        { value: 30, suffix: "%", label: "Average growth" },
        { value: 24, suffix: "/7", label: "Support & care" },
      ],
    },
    faq: {
      kicker: "FAQ",
      title: "Frequently asked questions",
      items: [
        { q: "How long does it take to build a website?", a: "Depending on scope, 3 to 12 weeks. A typical marketing site lands at 4–6 weeks; complex SaaS platforms or AI integrations at 8–12 weeks. You'll get a concrete timeline after our first strategy call." },
        { q: "What does a project at Joshi FusionTech Media cost?", a: "Our projects start at €2,500 for landing pages and scale up to €20,000+ for complex SaaS platforms or custom AI automation. A binding quote is built together with you during the free strategy call — no fixed packages, no hidden costs." },
        { q: "Do you offer AI automation?", a: "Yes — from chatbots and GPT integrations to full multi-agent workflows. We use cutting-edge LLMs (GPT-5, Claude, Gemini) and integrate them seamlessly with your existing tools (CRM, ERP, email, etc.)." },
        { q: "What kind of companies does Joshi FusionTech Media work with?", a: "We work with startups, SMEs, e-commerce brands, service businesses and growth-stage mid-market companies from Berlin, Germany and across the EU. Focus: companies serious about digital transformation." },
        { q: "Do you also work outside Berlin?", a: "Yes. Even though we're based in Berlin, we serve clients across Germany and the EU fully remote. On-site workshops in Berlin can be arranged on request." },
        { q: "What does the project flow look like?", a: "Five clear steps: (1) Discovery — we analyze goals & current state, (2) Strategy — we build a roadmap with milestones, (3) Design — UX/UI with a conversion focus, (4) Development — clean, performant implementation, (5) Launch & Optimization. Weekly demos, transparent communication, no black box." },
        { q: "Do you provide post-launch support?", a: "Yes — we offer flexible support packages (monthly, quarterly or on-demand). Updates, hosting, monitoring, performance optimisation and ongoing development are all part of our care plans." },
        { q: "Can you also optimise existing websites?", a: "Yes. We regularly run SEO audits, Core Web Vitals optimisations, conversion-rate improvements and redesigns of existing sites — from WordPress to Webflow to custom React apps." },
      ],
    },
    testimonials: {
      kicker: "Client signals",
      title: "What our clients say.",
      list: [
        { q: "Professional, trustworthy and innovative. Excellent collaboration from start to finish.", a: "Operations Lead", role: "FinTech SMB", r: 5 },
        { q: "From idea to live MVP in 32 days. Sharp design, sharp execution. Communication was crisp and predictable.", a: "Founder", role: "B2B SaaS", r: 5 },
        { q: "Finally an agency that understands engineering, not just slides. They pushed back where it mattered.", a: "CTO", role: "Healthcare Startup", r: 5 },
        { q: "The AI workflow they built saves our team several hours every single week. Clean handover, good documentation.", a: "Head of Operations", role: "Logistics", r: 5 },
        { q: "Solid technical SEO work. Took longer than promised initially, but the long-term result speaks for itself.", a: "Marketing Lead", role: "E-commerce", r: 4 },
        { q: "Rebranding was thorough — strategy, system, applied across pitch deck and website. Felt like a partner, not a vendor.", a: "Managing Director", role: "Boutique B2B", r: 5 },
        { q: "Calendly + AI chatbot in two weeks. Inbound qualification went from days to minutes. Pragmatic team.", a: "Sales Lead", role: "SaaS Mid-Market", r: 5 },
        { q: "Good roadmap workshop. We finally have named owners and quarterly reviews — that alone was worth it.", a: "CEO", role: "Industrial Mittelstand", r: 4 },
        { q: "Strong execution on the headless shop relaunch. Mobile checkout is finally a non-issue.", a: "Head of Digital", role: "DACH Lifestyle Brand", r: 5 },
      ],
    },
    cta: { title: "Ready for your next digital project?", subtitle: "Let's turn your ideas into digital success together.", btn: "Book a free strategy call" },
    founder: {
      kicker: "Meet the Founder",
      title: "The mind behind Joshi FusionTech Media is Rudrik Joshi.",
      intro: "Technologist, project manager and AI enthusiast based in Berlin. Rudrik founded Joshi FusionTech Media with a simple conviction: businesses deserve digital partners who think as strategically as they execute technically — and who treat AI as a real growth lever, not a buzzword.",
      body: "With deep experience in shipping scalable web platforms, integrating modern AI APIs (OpenAI, Claude, Gemini) and managing complex client projects, Rudrik combines hands-on engineering with crystal-clear communication. The mission: help companies across Berlin, Germany and the EU move their digital transformation forward — measurably — from strategy through design to launch.",
      role: "Owner & Founder · Berlin",
      cta: "More about Rudrik",
    },
    ecosystem: {
      kicker: "Digital Ecosystem",
      title: "One Partner. Complete Digital Transformation.",
      subtitle: "Our services don't live in silos — they compound. Each capability accelerates the next.",
      nodes: ["AI & Automation", "Web Development", "Digital Marketing", "Content & Branding", "Business Growth"],
    },
    about: {
      kicker: "Our Story",
      title: "Built to merge creativity, technology and business strategy.",
      subtitle: "Founded by Rudrik Joshi, Joshi FusionTech Media helps businesses grow through smart technology and modern digital solutions.",
      storyTitle: "Our Story — Joshi FusionTech Media",
      storyParas: [
        "Founded by Rudrik Joshi and inspired by a vision of innovation, creativity, and digital transformation, Joshi FusionTech Media was created to help businesses grow through smart technology and modern digital solutions.",
        "With a background in engineering, international technology management, operations, and project leadership, we recognized a growing challenge faced by many businesses: having great ideas but lacking the right digital presence, automation, and strategic technology support to scale efficiently.",
        "That vision led to the creation of Joshi FusionTech Media — a company built on the belief that technology should not only look impressive but also deliver measurable business impact.",
      ],
      approachTitle: "Our approach combines",
      approachItems: [
        "Website Development",
        "Digital Marketing",
        "AI & Automation Solutions",
        "Branding & Media Design",
        "Business Process Optimization",
        "EdTech & Innovation Support",
      ],
      focusTitle: "We focus on more than just design",
      focusIntro: "We focus on creating systems that help businesses:",
      focusItems: [
        "improve visibility",
        "automate operations",
        "strengthen customer engagement",
        "accelerate growth in the digital world",
      ],
      sloganLead: "Guided by our slogan",
      slogan: "Automate. Innovate. Elevate.",
      closing: "We continue building solutions that merge creativity, technology, and business strategy into one unified experience. Whether it is a professional website, a digital marketing campaign, AI-powered automation, or a complete brand transformation — our mission is to help businesses move confidently into the future.",
      foundersTitle: "Inhaber",
      founderName: "Rudrik Joshi",
      founderRole: "Inhaber | Joshi FusionTech Media",
      pillarsTitle: "What we stand for",
      pillars: [
        { t: "Trust", d: "We build reliable and secure solutions." },
        { t: "Innovation", d: "We use AI and technology to drive innovation." },
        { t: "Results", d: "We focus on measurable results and growth." },
        { t: "Partnership", d: "We grow together with our clients." },
      ],
    },
    portfolio: {
      kicker: "Selected work",
      title: "A glimpse at recent builds.",
      subtitle: "Confidential clients, public outcomes. Full case studies available on request.",
      cases: [
        { tag: "AI Automation", title: "Customer-support AI for a DACH e-commerce brand", metric: "−68% support tickets" },
        { tag: "Web Platform", title: "B2B SaaS dashboard for a logistics startup", metric: "32 days to MVP" },
        { tag: "Marketing", title: "SEO + content engine for a fintech challenger", metric: "+212% organic traffic" },
        { tag: "Consulting", title: "Architecture audit for a Series-A health tech", metric: "−40% infra cost" },
      ],
    },
    contact: {
      kicker: "Contact",
      title: "Let's build something seamless.",
      subtitle: "Drop a message or book a 30-min call. We typically reply within a business day.",
      name: "Your name",
      email: "Email",
      company: "Company (optional)",
      phone: "Phone (optional)",
      service: "Service of interest",
      message: "Tell us about your project",
      send: "Send message",
      sending: "Sending...",
      success: "Thanks! We'll get back to you shortly.",
      error: "Something went wrong. Please try again.",
      whatsapp: "Chat on WhatsApp",
      bookCall: "Book a free call",
      pickService: "Pick a service",
      consentLabel: "I have read and agree to the",
      consentLink: "privacy policy",
      consentRequired: "Please accept the privacy policy to continue.",
      captchaLabel: "Human verification",
      captchaError: "Please complete the verification.",
    },
    footer: {
      description: "We combine technology, creativity and strategy to build digital solutions that grow businesses sustainably.",
      tagline: "Automate. Innovate. Elevate.",
      services: "Services",
      company: "Company",
      legal: "Legal",
      contact: "Contact",
      impressum: "Imprint",
      privacy: "Privacy",
      terms: "Terms",
      rights: "All rights reserved.",
      address: "Berlin, Germany",
      careers: "Careers",
      references: "References",
      blog: "Blog",
      svc_marketing: "Digital Marketing",
      svc_content: "Content & Branding",
      svc_web: "Web Development",
      svc_ai: "AI & Automation",
      svc_brand: "Brand Design",
      svc_strategy: "Strategy & Consulting",
    },
    chatbot: {
      title: "Fusion AI",
      subtitle: "Ask anything or get a quote",
      placeholder: "Type your message...",
      send: "Send",
      hint: "Powered by GPT-5.2",
      greeting: "Hi! I'm Fusion. Chat freely with me, or start a 60-second lead qualification to get a tailored quote.",
      chip_ai: "Just chat",
      chip_qual: "Get a quote",
      qual: {
        askName: "Perfect. What's your first name?",
        askEmail: "Nice to meet you, {name}. What's the best email to reach you at?",
        askService: "Great. Which of our services matches your need best?",
        askBudget: "Thanks. What's the rough investment range you have in mind for this project?",
        askTimeline: "Almost done — when would you like to see this live or start working on it?",
        askCompany: "Optional: which company are you with? Type it or tap Skip.",
        askPhone: "Optional: a phone number for quick callbacks?",
        askDescription: "Optional: describe your project in one or two sentences.",
        skip: "Skip",
        summary: "Perfect, {name} — I've captured everything.",
        summaryEmail: "We'll email you at {email}, usually within a few hours.",
        nextCta: "Would you like to book a call or continue on WhatsApp?",
        submitting: "Sending your details to the team...",
        submitted: "Sent! We just emailed the team at info@joshifusiontech.de and I've saved your details.",
        failed: "I couldn't reach the team just now. Please try the contact form or write to info@joshifusiontech.de directly.",
        invalidEmail: "Hmm, that doesn't look like an email. Could you double-check?",
        book: "Book a 30-min call",
        whatsapp: "Chat on WhatsApp",
        contact: "Open contact form",
        restart: "Start over",
        services: ["AI & Automation", "Web Development", "Digital Marketing", "Content & Branding", "Brand Design", "Strategy & Consulting"],
        budgets: ["< €5k", "€5–10k", "€10–25k", "€25–50k", "€50k+"],
        timelines: ["ASAP", "0–1 month", "1–3 months", "3–6 months", "Just exploring"],
      },
    },
  },
  de: {
    nav: { home: "Start", services: "Leistungen", about: "Über uns", portfolio: "Portfolio", blog: "Blog", contact: "Kontakt", book: "Termin" },
    common: { learnMore: "Mehr erfahren", getStarted: "Loslegen", talkToUs: "Schreiben", readMore: "Weiterlesen" },
    hero: {
      kicker: "Deutschland · KI · Automatisierung",
      title: "Automate. Innovate. Elevate.",
      subtitle: "Wir transformieren Unternehmen durch KI-Automatisierung, Webentwicklung, digitales Marketing, Branding und skalierbare Lösungen — made in Berlin.",
      cta1: "Kostenloses Beratungsgespräch",
      cta2: "Unsere Leistungen",
      stat1: "Projekte umgesetzt",
      stat2: "Reaktionszeit",
      stat3: "Kundentreue",
      trustedBy: "Vertraut von ambitionierten Teams",
      trust: ["Berlin Based", "AI-Powered Solutions", "End-to-End Digital Services"],
    },
    services: {
      kicker: "Was wir tun",
      title: "Digitale Lösungen, die Wirkung zeigen.",
      subtitle: "Von KI-Agenten bis hin zu konvertierenden Web-Plattformen — ein Partner, end-to-end.",
      ai: { title: "AI & Automation", desc: "Automatisierte Prozesse, intelligente Workflows und Effizienzsteigerung." },
      web: { title: "Web Development", desc: "Moderne, schnelle und skalierbare Websites für Unternehmen jeder Größe." },
      marketing: { title: "Digital Marketing", desc: "Datengesteuerte SEO, Content-Strategien und Performance-Marketing für nachhaltiges Wachstum." },
      content: { title: "Content & Branding", desc: "Starke Markenidentitäten, kreativer Content und überzeugendes Storytelling." },
      brand: { title: "Brand Design", desc: "Visuelle Identitäten, die Vertrauen schaffen und Wiedererkennung fördern." },
      consulting: { title: "Strategie & Beratung", desc: "Digitale Strategien für langfristigen Unternehmenserfolg." },
      capabilities: "Kompetenzen",
    },
    process: {
      kicker: "So arbeiten wir",
      title: "Ein strukturierter Ablauf. Klare Ergebnisse.",
      steps: [
        { t: "Discovery", d: "Wir analysieren Ziele und Potenziale." },
        { t: "Strategie", d: "Wir entwickeln eine digitale Roadmap." },
        { t: "Design", d: "Modernes UX/UI Design mit Fokus auf Conversion." },
        { t: "Entwicklung", d: "Saubere, performante und skalierbare Umsetzung." },
        { t: "Launch", d: "Testing, Optimierung und erfolgreicher Go-Live." },
      ],
    },
    whyUs: {
      kicker: "Warum wir",
      title: "Warum Joshi FusionTech Media?",
      intro: "Wir kombinieren Technologie, Kreativität und Strategie, um digitale Lösungen zu entwickeln, die Unternehmen nachhaltig wachsen lassen.",
      points: [
        { t: "Individuelle Lösungen", d: "Maßgeschneidert auf dein Geschäftsmodell — keine Templates, keine Kompromisse." },
        { t: "Moderne Technologien", d: "Aktueller Tech-Stack: React, Python, KI-APIs und Cloud-Native Architektur." },
        { t: "KI-gestützte Automatisierung", d: "Workflows, die nicht nur sparen, sondern aktiv Mehrwert generieren." },
        { t: "Transparente Kommunikation", d: "Wöchentliche Demos, klare Roadmap, keine Black Box." },
        { t: "Langfristige Partnerschaften", d: "Wir bauen Beziehungen, nicht nur Projekte — Support nach dem Launch inklusive." },
      ],
    },
    stats: {
      kicker: "Zahlen, die zählen",
      title: "Ergebnisse, auf die wir stolz sind.",
      items: [
        { value: 50, suffix: "+", label: "Projekte abgeschlossen" },
        { value: 95, suffix: "%", label: "Kundenzufriedenheit" },
        { value: 30, suffix: "%", label: "Durchschnittliches Wachstum" },
        { value: 24, suffix: "/7", label: "Support & Betreuung" },
      ],
    },
    faq: {
      kicker: "FAQ",
      title: "Häufig gestellte Fragen",
      items: [
        { q: "Wie lange dauert die Entwicklung einer Website?", a: "Je nach Umfang zwischen 3 und 12 Wochen. Ein klassisches Marketing-Site liegt bei 4–6 Wochen, komplexe SaaS-Plattformen oder KI-Integrationen bei 8–12 Wochen. Du bekommst eine konkrete Timeline nach unserem ersten Strategiegespräch." },
        { q: "Was kostet ein Projekt bei Joshi FusionTech Media?", a: "Unsere Projekte starten ab €2.500 für Landingpages und reichen bis €20.000+ für komplexe SaaS-Plattformen oder maßgeschneiderte KI-Automatisierung. Ein verbindliches Angebot erstellst du gemeinsam mit uns im kostenfreien Strategiegespräch — keine Pakete-Pflicht, keine versteckten Kosten." },
        { q: "Bieten Sie AI-Automatisierung an?", a: "Ja — von Chatbots über GPT-Integrationen bis hin zu kompletten Multi-Agent-Workflows. Wir nutzen modernste LLMs (GPT-5, Claude, Gemini) und verbinden sie nahtlos mit deinen bestehenden Tools (CRM, ERP, E-Mail, etc.)." },
        { q: "Welche Art von Unternehmen betreut Joshi FusionTech Media?", a: "Wir arbeiten mit Startups, KMU, E-Commerce-Brands, Dienstleistern und wachstumsstarken Mittelständlern aus Berlin, Deutschland und der EU. Schwerpunkt: Unternehmen, die ihre digitale Transformation ernsthaft angehen wollen." },
        { q: "Arbeiten Sie auch außerhalb von Berlin?", a: "Ja. Auch wenn wir in Berlin sitzen, betreuen wir Kunden in ganz Deutschland und der EU vollständig remote. Vor-Ort-Workshops in Berlin sind auf Wunsch möglich." },
        { q: "Wie sieht der Projektablauf aus?", a: "Fünf klare Schritte: (1) Discovery — wir analysieren Ziele & Status quo, (2) Strategie — wir bauen eine Roadmap mit Meilensteinen, (3) Design — UX/UI mit Conversion-Fokus, (4) Entwicklung — saubere, performante Umsetzung, (5) Launch & Optimierung. Wöchentliche Demos, klare Kommunikation, keine Black Box." },
        { q: "Bieten Sie Support nach Projektabschluss an?", a: "Ja — wir bieten flexible Support-Pakete (Monatlich, Quartal oder On-Demand). Updates, Hosting, Monitoring, Performance-Optimierung und Weiterentwicklung sind alle Bestandteile unserer Care-Plans." },
        { q: "Können Sie auch bestehende Websites optimieren?", a: "Ja. Wir machen regelmäßig SEO-Audits, Core-Web-Vitals-Optimierungen, Conversion-Rate-Verbesserungen und Re-Designs bestehender Seiten — von WordPress über Webflow bis zu maßgeschneiderten React-Apps." },
      ],
    },
    testimonials: {
      kicker: "Was Kunden sagen",
      title: "Was unsere Kunden sagen.",
      list: [
        { q: "Professionell, vertrauenswürdig und innovativ. Hervorragende Zusammenarbeit von Anfang bis Ende.", a: "Operations Lead", role: "FinTech KMU", r: 5 },
        { q: "Von der Idee zum Live-MVP in 32 Tagen. Klares Design, scharfe Umsetzung. Kommunikation war sauber und planbar.", a: "Gründer", role: "B2B SaaS", r: 5 },
        { q: "Endlich eine Agentur, die Engineering versteht — nicht nur Slides. Sie haben da gepusht, wo es wichtig war.", a: "CTO", role: "Health-Startup", r: 5 },
        { q: "Der AI-Workflow spart unserem Team jede Woche mehrere Stunden. Saubere Übergabe, gute Dokumentation.", a: "Head of Operations", role: "Logistik", r: 5 },
        { q: "Solide Technical-SEO-Arbeit. Es dauerte initial länger als angekündigt — das Ergebnis spricht jetzt aber für sich.", a: "Marketing Lead", role: "E-Commerce", r: 4 },
        { q: "Rebranding war konsequent — Strategie, System, angewendet auf Pitch-Deck und Website. Hat sich wie Partner angefühlt, nicht wie Dienstleister.", a: "Geschäftsführer", role: "Boutique B2B", r: 5 },
        { q: "Calendly + AI-Chatbot in zwei Wochen. Inbound-Qualifizierung von Tagen auf Minuten. Pragmatisches Team.", a: "Sales Lead", role: "SaaS Mid-Market", r: 5 },
        { q: "Guter Roadmap-Workshop. Wir haben endlich benannte Owner:innen und Quartals-Reviews — das allein war es schon wert.", a: "CEO", role: "Industrieller Mittelstand", r: 4 },
        { q: "Starke Umsetzung beim Headless-Shop-Relaunch. Mobiler Checkout ist endlich kein Thema mehr.", a: "Head of Digital", role: "DACH Lifestyle-Marke", r: 5 },
      ],
    },
    cta: { title: "Bereit für Ihr nächstes digitales Projekt?", subtitle: "Lassen Sie uns gemeinsam Ihre Ideen in digitale Erfolge verwandeln.", btn: "Kostenloses Beratungsgespräch vereinbaren" },
    founder: {
      kicker: "Meet the Founder",
      title: "Hinter Joshi FusionTech Media steht Rudrik Joshi.",
      intro: "Technologe, Projektmanager und KI-Enthusiast aus Berlin. Rudrik gründete Joshi FusionTech Media mit einer einfachen Überzeugung: Unternehmen verdienen digitale Partner, die genauso strategisch wie technisch denken — und die KI nicht als Buzzword, sondern als echten Wachstumshebel einsetzen.",
      body: "Mit Erfahrung in der Entwicklung skalierbarer Web-Plattformen, der Integration moderner KI-APIs (OpenAI, Claude, Gemini) und der Steuerung anspruchsvoller Kundenprojekte verbindet Rudrik tiefes technisches Verständnis mit klarer Kommunikation. Die Mission: Unternehmen aus Berlin, Deutschland und der EU dabei zu helfen, ihre digitale Transformation messbar voranzutreiben — von der Strategie über das Design bis zum Launch.",
      role: "Inhaber & Founder · Berlin",
      cta: "Mehr über Rudrik",
    },
    ecosystem: {
      kicker: "Digital Ecosystem",
      title: "Ein Partner. Komplette digitale Transformation.",
      subtitle: "Unsere Leistungen leben nicht in Silos — sie greifen ineinander. Jede Fähigkeit beschleunigt die nächste.",
      nodes: ["KI & Automatisierung", "Webentwicklung", "Digital Marketing", "Content & Branding", "Business Wachstum"],
    },
    about: {
      kicker: "Unsere Geschichte",
      title: "Kreativität, Technologie und Business-Strategie — vereint.",
      subtitle: "Gegründet von Rudrik Joshi, hilft Joshi FusionTech Media Unternehmen, durch smarte Technologie und moderne digitale Lösungen zu wachsen.",
      storyTitle: "Unsere Geschichte — Joshi FusionTech Media",
      storyParas: [
        "Gegründet von Rudrik Joshi und inspiriert von einer Vision aus Innovation, Kreativität und digitaler Transformation, wurde Joshi FusionTech Media ins Leben gerufen, um Unternehmen durch smarte Technologie und moderne digitale Lösungen wachsen zu lassen.",
        "Mit einem Hintergrund in Engineering, internationalem Technologiemanagement, Operations und Projektleitung erkannten wir eine wachsende Herausforderung vieler Unternehmen: Sie haben grossartige Ideen, aber keine passende digitale Präsenz, Automatisierung und strategische Technologieunterstützung, um effizient zu skalieren.",
        "Daraus entstand Joshi FusionTech Media — ein Unternehmen, das auf der Überzeugung basiert, dass Technologie nicht nur beeindruckend aussehen, sondern auch messbaren Business-Impact liefern soll.",
      ],
      approachTitle: "Unser Ansatz kombiniert",
      approachItems: [
        "Webentwicklung",
        "Digital Marketing",
        "KI & Automatisierungslösungen",
        "Branding & Mediendesign",
        "Geschäftsprozess-Optimierung",
        "EdTech & Innovationsunterstützung",
      ],
      focusTitle: "Wir fokussieren mehr als nur Design",
      focusIntro: "Wir schaffen Systeme, die Unternehmen helfen:",
      focusItems: [
        "Sichtbarkeit zu verbessern",
        "Abläufe zu automatisieren",
        "Kundenbindung zu stärken",
        "Wachstum in der digitalen Welt zu beschleunigen",
      ],
      sloganLead: "Geleitet von unserem Leitspruch",
      slogan: "Automate. Innovate. Elevate.",
      closing: "Wir bauen weiter Lösungen, die Kreativität, Technologie und Business-Strategie zu einem nahtlosen Erlebnis verbinden. Ob professionelle Website, Digital-Marketing-Kampagne, KI-gestützte Automatisierung oder komplette Markentransformation — unsere Mission ist es, Unternehmen sicher in die Zukunft zu bringen.",
      foundersTitle: "Inhaber",
      founderName: "Rudrik Joshi",
      founderRole: "Inhaber | Joshi FusionTech Media",
      pillarsTitle: "Wofür wir stehen",
      pillars: [
        { t: "Vertrauen", d: "Wir bauen zuverlässige und sichere Lösungen." },
        { t: "Innovation", d: "Wir nutzen KI und Technologie für echte Innovation." },
        { t: "Ergebnisse", d: "Wir fokussieren uns auf messbare Resultate und Wachstum." },
        { t: "Partnerschaft", d: "Wir wachsen gemeinsam mit unseren Kund:innen." },
      ],
    },
    portfolio: {
      kicker: "Ausgewählte Arbeiten",
      title: "Ein Blick auf aktuelle Builds.",
      subtitle: "Vertrauliche Kunden, öffentliche Outcomes. Vollständige Case Studies auf Anfrage.",
      cases: [
        { tag: "KI-Automatisierung", title: "Customer-Support-KI für eine DACH-E-Commerce-Marke", metric: "−68 % Support-Tickets" },
        { tag: "Web-Plattform", title: "B2B-SaaS-Dashboard für ein Logistik-Startup", metric: "32 Tage bis MVP" },
        { tag: "Marketing", title: "SEO- + Content-Engine für einen Fintech-Challenger", metric: "+212 % Organic Traffic" },
        { tag: "Beratung", title: "Architektur-Audit für ein Series-A Health-Tech", metric: "−40 % Infra-Kosten" },
      ],
    },
    contact: {
      kicker: "Kontakt",
      title: "Lass uns etwas Nahtloses bauen.",
      subtitle: "Schreib eine Nachricht oder buche ein 30-Min-Gespräch. Antwort meist innerhalb eines Werktages.",
      name: "Dein Name",
      email: "E-Mail",
      company: "Firma (optional)",
      phone: "Telefon (optional)",
      service: "Gewünschte Leistung",
      message: "Erzähl uns von deinem Projekt",
      send: "Nachricht senden",
      sending: "Sende...",
      success: "Danke! Wir melden uns in Kürze.",
      error: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
      whatsapp: "WhatsApp-Chat",
      bookCall: "Kostenloses Gespräch",
      pickService: "Leistung wählen",
      consentLabel: "Ich habe die",
      consentLink: "Datenschutzerklärung",
      consentSuffix: "gelesen und stimme ihr zu.",
      consentRequired: "Bitte akzeptiere die Datenschutzerklärung, um fortzufahren.",
      captchaLabel: "Menschliche Verifizierung",
      captchaError: "Bitte schliesse die Verifizierung ab.",
    },
    footer: {
      description: "Wir kombinieren Technologie, Kreativität und Strategie, um digitale Lösungen zu entwickeln, die Unternehmen nachhaltig wachsen lassen.",
      tagline: "Automate. Innovate. Elevate.",
      services: "Leistungen",
      company: "Unternehmen",
      legal: "Rechtliches",
      contact: "Kontakt",
      impressum: "Impressum",
      privacy: "Datenschutz",
      terms: "AGB",
      rights: "Alle Rechte vorbehalten.",
      address: "Berlin, Deutschland",
      careers: "Karriere",
      references: "Referenzen",
      blog: "Blog",
      svc_marketing: "Digital Marketing",
      svc_content: "Content & Branding",
      svc_web: "Web Development",
      svc_ai: "AI & Automation",
      svc_brand: "Brand Design",
      svc_strategy: "Strategie & Beratung",
    },
    chatbot: {
      title: "Fusion KI",
      subtitle: "Frag mich oder hol dir ein Angebot",
      placeholder: "Nachricht eingeben...",
      send: "Senden",
      hint: "Powered by GPT-5.2",
      greeting: "Hi! Ich bin Fusion. Du kannst frei mit mir chatten — oder in 60 Sekunden ein individuelles Angebot anfordern.",
      chip_ai: "Nur chatten",
      chip_qual: "Angebot einholen",
      qual: {
        askName: "Perfekt. Wie ist dein Vorname?",
        askEmail: "Freut mich, {name}. Unter welcher E-Mail-Adresse erreichen wir dich am besten?",
        askService: "Super. Welche unserer Leistungen passt am besten zu deinem Vorhaben?",
        askBudget: "Danke. Welches ungefähre Budget hast du für dieses Projekt im Kopf?",
        askTimeline: "Fast fertig — wann möchtest du starten oder es live sehen?",
        askCompany: "Optional: Für welches Unternehmen arbeitest du? Eintippen oder Überspringen.",
        askPhone: "Optional: Eine Telefonnummer für schnelle Rückfragen?",
        askDescription: "Optional: Beschreibe dein Projekt in ein bis zwei Sätzen.",
        skip: "Überspringen",
        summary: "Perfekt, {name} — ich habe alles notiert.",
        summaryEmail: "Wir melden uns bei {email}, meist innerhalb weniger Stunden.",
        nextCta: "Willst du direkt einen Termin buchen oder auf WhatsApp weiterschreiben?",
        submitting: "Deine Daten werden ans Team gesendet...",
        submitted: "Erledigt! Wir haben das Team unter info@joshifusiontech.de informiert und deine Daten gespeichert.",
        failed: "Ich konnte das Team gerade nicht erreichen. Nutze bitte das Kontaktformular oder schreib direkt an info@joshifusiontech.de.",
        invalidEmail: "Hmm, das sieht nicht wie eine E-Mail aus. Könntest du sie nochmal prüfen?",
        book: "30-Min-Termin buchen",
        whatsapp: "Auf WhatsApp weiter",
        contact: "Kontaktformular öffnen",
        restart: "Neu starten",
        services: ["KI & Automatisierung", "Webentwicklung", "Digital Marketing", "Content & Branding", "Brand Design", "Strategie & Beratung"],
        budgets: ["< 5.000 €", "5–10.000 €", "10–25.000 €", "25–50.000 €", "50.000 € +"],
        timelines: ["Sofort", "0–1 Monat", "1–3 Monate", "3–6 Monate", "Nur erkunden"],
      },
    },
  },
};

const I18nContext = createContext({ locale: "de", t: dict.de, setLocale: () => {} });

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => {
    if (typeof window === "undefined") return "de";
    // Honor previous explicit choice if user has interacted before;
    // otherwise default to German (primary market).
    return localStorage.getItem("jftm.locale") || "de";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("jftm.locale", locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = (l) => setLocaleState(l === "en" ? "en" : "de");

  return (
    <I18nContext.Provider value={{ locale, setLocale, t: dict[locale] || dict.de }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
